import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { TabFilter } from '@travellegacy/nexops-design-system';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@travellegacy/nexops-design-system';
import { toast } from '@/shared/lib/toast';
import { articlesService, type BlogArticle } from '../services/articles.service';
import { formatDate } from '@/shared/lib/utils';

type ArticleStatus = 'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'PUBLISHED';

const STATUS_CONFIG: Record<
  Exclude<ArticleStatus, 'ALL'>,
  { label: string; variant: 'default' | 'success' | 'warning' | 'error' }
> = {
  PENDING: { label: 'Pendente', variant: 'warning' },     // Amarelo
  REJECTED: { label: 'Rejeitado', variant: 'error' },     // Vermelho
  APPROVED: { label: 'Aprovado', variant: 'default' },    // Azul
  PUBLISHED: { label: 'Publicado', variant: 'success' },  // Verde
};

export default function ArticlesPage() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<ArticleStatus>('ALL');

  // Carregar artigos
  useEffect(() => {
    loadArticles();
  }, []);

  // Filtrar artigos quando o status muda
  useEffect(() => {
    if (selectedStatus === 'ALL') {
      setFilteredArticles(articles);
    } else {
      setFilteredArticles(articles.filter((a) => a.status === selectedStatus));
    }
  }, [selectedStatus, articles]);

  const loadArticles = async () => {
    try {
      setLoading(true);
      const response = await articlesService.listArticles({ limit: 100 });
      setArticles(response.articles);
    } catch (error) {
      toast.error('Erro ao carregar artigos', error instanceof Error ? error.message : undefined);
    } finally {
      setLoading(false);
    }
  };

  const openArticle = (article: BlogArticle) => {
    navigate(`/blog/articles/${article.id}`);
  };

  const tabFilters: { label: string; value: ArticleStatus; count?: number }[] = [
    { label: 'Todos', value: 'ALL', count: articles.length },
    {
      label: 'Pendentes',
      value: 'PENDING',
      count: articles.filter((a) => a.status === 'PENDING').length,
    },
    {
      label: 'Aprovados',
      value: 'APPROVED',
      count: articles.filter((a) => a.status === 'APPROVED').length,
    },
    {
      label: 'Rejeitados',
      value: 'REJECTED',
      count: articles.filter((a) => a.status === 'REJECTED').length,
    },
    {
      label: 'Publicados',
      value: 'PUBLISHED',
      count: articles.filter((a) => a.status === 'PUBLISHED').length,
    },
  ];

  return (
    <div className="container mx-auto py-8 px-4 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Artigos do Blog</h1>
        <p className="text-muted-foreground mt-2">
          Gerencie os artigos criados automaticamente pelo Nexus às 09:00 diariamente
        </p>
      </div>

      {/* Tabela de artigos */}
      <Card>
        <CardHeader>
          <TabFilter
            tabs={tabFilters}
            currentTab={selectedStatus}
            onChange={(value) => setSelectedStatus(value as ArticleStatus)}
          />
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum artigo encontrado</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Gerado em</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Título</TableHead>
                  <TableHead>Descrição</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredArticles.map((article) => (
                  <TableRow
                    key={article.id}
                    className="cursor-pointer hover:bg-accent/50"
                    onClick={() => openArticle(article)}
                  >
                    <TableCell className="whitespace-nowrap">
                      {formatDate(article.generatedAt)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_CONFIG[article.status].variant}>
                        {STATUS_CONFIG[article.status].label}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">
                      {article.title}
                    </TableCell>
                    <TableCell className="max-w-md">
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {article.excerpt || 'Sem descrição'}
                      </p>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
